/**
 * utils/string-utils.ts
 *
 * Utility functions for string manipulation in the application
 *
 * This module provides utilities for handling HTML content with entities,
 * using html-react-parser for client-side rendering and fallback methods
 * for server-side processing.
 */

import parse from 'html-react-parser';

/**
 * Parses HTML content including entities using html-react-parser
 * This handles all HTML entities automatically and returns React elements
 *
 * Use this when you want to render HTML content as React components
 *
 * @example
 * // Input: "<p>Hello &amp; welcome to UIC&#039;s platform</p>"
 * // Output: React elements rendering: <p>Hello & welcome to UIC's platform</p>
 * const content = "<p>Hello &amp; welcome to UIC&#039;s platform</p>";
 * return <div>{parseHtml(content)}</div>;
 *
 * @param html The HTML string containing entities to parse
 * @returns React elements with parsed content
 */
export function parseHtml(html: string): React.ReactNode {
  if (!html || typeof html !== 'string') {
    return html;
  }

  // html-react-parser automatically handles HTML entities and parses the structure
  return parse(html);
}

/**
 * Highlights matched text in a string by wrapping it in a span with a specific class
 * This is used for search highlighting in the UI
 *
 * @param text The text to search within
 * @param searchTerm The search term to highlight
 * @param highlightClass CSS class to apply to the highlighted text
 * @returns HTML string with matched text wrapped in highlighted spans
 */
export function highlightMatchedText(
  text: string,
  searchTerm: string,
  highlightClass: string = 'bg-yellow-500/20'
): string {
  if (!text || !searchTerm) {
    return text;
  }

  // Escape special regex characters to avoid errors
  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Create a case-insensitive regular expression
  const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');

  // Replace matches with the highlighted version
  return text.replace(regex, `<span class="${highlightClass}">$1</span>`);
}

/**
 * Truncates text to a specified length and adds ellipsis if needed
 *
 * @param text Text to truncate
 * @param maxLength Maximum length before truncation
 * @param ellipsis String to append when truncated (default: '...')
 * @returns Truncated text with ellipsis if needed
 */
export function truncateText(text: string, maxLength: number, ellipsis: string = '...'): string {
  if (!text || text.length <= maxLength) {
    return text;
  }

  return text.slice(0, maxLength) + ellipsis;
}

// /**
//  * Creates initials from a name (e.g., "John Doe" becomes "JD")
//  *
//  * @param name Full name
//  * @param maxInitials Maximum number of initials to return
//  * @returns String of initials
//  */
// export function getInitials(name: string, maxInitials: number = 2): string {
//   if (!name) return '';

//   const parts = name.split(/\s+/);
//   const initials = parts
//     .filter((part) => part.length > 0)
//     .map((part) => part[0].toUpperCase())
//     .slice(0, maxInitials)
//     .join('');

//   return initials;
// }

/**
 * Creates a URL-safe slug from a string by removing special characters
 * and replacing spaces with hyphens
 */
export function slugify(text: string): string {
  return (
    text
      .toLowerCase()
      .trim()
      // Remove accents and special characters
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      // Replace spaces and underscores with hyphens
      .replace(/[\s_]+/g, '-')
      // Remove all non-alphanumeric characters except hyphens
      .replace(/[^a-z0-9-]/g, '')
      // Remove multiple consecutive hyphens
      .replace(/-+/g, '-')
      // Remove leading and trailing hyphens
      .replace(/^-+|-+$/g, '')
  );
}

/**
 * Converts a slug back to a readable title
 */
export function unslugify(slug: string): string {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Decode HTML entities in strings
 * Handles common entities like &amp;, &apos;, &quot;, &lt;, &gt;
 *
 * @param text Text containing HTML entities
 * @returns Text with decoded HTML entities
 */
export function decodeHtmlEntities(text: string): string {
  if (!text || typeof text !== 'string') {
    return text;
  }

  return text
    .replace(/&amp;/g, '&')
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/');
}
